/**
 * Find similar tours based on content-based filtering with title matching and controlled randomness
 * @param {Array} tours - Array of all tours
 * @param {Object} currentTour - The current tour being viewed
 * @param {Number} limit - Number of tours to return
 * @returns {Array} - Array of similar tours
 */
export function findSimilarTours(tours, currentTour, limit = 4) {
  if (!currentTour || !tours || tours.length === 0) {
    return [];
  }

  // Filter out the current tour
  const otherTours = tours.filter(tour => tour.id !== currentTour.id);
  
  // Calculate similarity scores for each tour
  const toursWithScores = otherTours.map(tour => {
    let score = 0;
    
    // Title similarity (using word overlap)
    const currentTourWords = currentTour.title.toLowerCase().split(/\s+/)
      .filter(word => word.length > 3); // Ignore short words
    const tourWords = tour.title.toLowerCase().split(/\s+/)
      .filter(word => word.length > 3);
    
    // Calculate word overlap
    const commonWords = currentTourWords.filter(word => tourWords.includes(word));
    score += commonWords.length * 3; // Heavier weight for title matches
    
    // Location similarity
    if (tour.location === currentTour.location) {
      score += 5;
    } else if (tour.location && currentTour.location) {
      const currentLocWords = currentTour.location.toLowerCase().split(/\s+/);
      const tourLocWords = tour.location.toLowerCase().split(/\s+/);
      
      const commonLocWords = currentLocWords.filter(word => 
        tourLocWords.includes(word) && word.length > 3);
      
      score += commonLocWords.length * 2;
    }
    
    // Duration similarity
    const daysDiff = Math.abs(tour.days - currentTour.days);
    score += Math.max(5 - daysDiff, 0);
    
    // Price range similarity
    const priceDiffPercent = Math.abs(tour.price - currentTour.price) / currentTour.price;
    if (priceDiffPercent < 0.1) score += 4; // Very similar price (±10%)
    else if (priceDiffPercent < 0.25) score += 3; // Similar price (±25%)
    else if (priceDiffPercent < 0.5) score += 1; // Somewhat similar price (±50%)
    
    // Season similarity if available
    if (tour.season && currentTour.season && 
        tour.season.toLowerCase() === currentTour.season.toLowerCase()) {
      score += 3;
    }
    
    // Activity type similarity
    if (tour.activities && currentTour.activities) {
      const tourActivities = Array.isArray(tour.activities) 
        ? tour.activities 
        : tour.activities.split(',').map(a => a.trim());
      
      const currentActivities = Array.isArray(currentTour.activities)
        ? currentTour.activities
        : currentTour.activities.split(',').map(a => a.trim());
      
      const commonActivities = tourActivities.filter(act => 
        currentActivities.includes(act));
      
      score += commonActivities.length * 2;
    }
    
    // Add a small random factor (between 0-2 points) for diversity
    const randomFactor = Math.random() * 2;
    score += randomFactor;
    
    return { ...tour, score };
  });
  
  // Sort by similarity score (descending)
  toursWithScores.sort((a, b) => b.score - a.score);
  
  // Take top N results
  return toursWithScores.slice(0, limit);
}

/**
 * Get hybrid recommendations combining content-based and collaborative filtering
 * with title matching and controlled randomness
 * @param {Array} tours - Array of all tours
 * @param {Object} currentTour - The current tour being viewed
 * @param {Array} userBookings - User's booking history
 * @param {Object} user - User object
 * @param {Number} limit - Number of tours to return
 * @returns {Array} - Array of recommended tours
 */
export function getHybridRecommendations(tours, currentTour, userBookings, user, limit = 4) {
  // Get content-based recommendations
  const contentBasedRecs = findSimilarTours(tours, currentTour, limit * 2);
  
  // Get collaborative filtering recommendations (if we have user data)
  const bookedTourIds = userBookings.map(booking => booking.tourId);
  const userBasedRecs = [];
  
  if (bookedTourIds.length > 0) {
    // Find similar users who booked the same tours
    // This is a simplified collaborative approach:
    // - Find tours that were booked by others who booked similar tours as this user
    // - Exclude tours the user has already booked
    const potentialRecs = tours.filter(tour => 
      !bookedTourIds.includes(tour.id) && 
      tour.id !== currentTour.id
    );
    
    // Sort by popularity for simplicity
    potentialRecs.sort((a, b) => (b.reviews || 0) - (a.reviews || 0));
    
    // Add top recommendations
    userBasedRecs.push(...potentialRecs.slice(0, limit));
  }
  
  // Combine recommendations with weights
  const combinedRecs = [...contentBasedRecs, ...userBasedRecs];
  
  // Add a title match boost and randomization factor
  combinedRecs.forEach(tour => {
    // Title similarity boost
    const titleSimilarity = calculateTitleSimilarity(tour.title, currentTour.title);
    tour.score = (tour.score || 0) + (titleSimilarity * 3);
    
    // Add randomization (controlled randomness) - higher for lower ranked items
    const randomIndex = combinedRecs.indexOf(tour);
    const randomBoost = (Math.random() * 2) * (randomIndex / combinedRecs.length);
    tour.score += randomBoost;
  });
  
  // Sort by final score
  combinedRecs.sort((a, b) => (b.score || 0) - (a.score || 0));
  
  // Remove duplicates
  const uniqueRecIds = [];
  const uniqueRecs = [];
  
  for (const tour of combinedRecs) {
    if (!uniqueRecIds.includes(tour.id)) {
      uniqueRecIds.push(tour.id);
      uniqueRecs.push(tour);
      
      if (uniqueRecs.length >= limit) {
        break;
      }
    }
  }
  
  return uniqueRecs;
}

/**
 * Calculate similarity between two titles
 * @param {String} title1 - First title
 * @param {String} title2 - Second title
 * @returns {Number} - Similarity score (0-1)
 */
function calculateTitleSimilarity(title1, title2) {
  if (!title1 || !title2) return 0;
  
  const words1 = title1.toLowerCase().split(/\s+/).filter(w => w.length > 3);
  const words2 = title2.toLowerCase().split(/\s+/).filter(w => w.length > 3);
  
  if (words1.length === 0 || words2.length === 0) return 0;
  
  const commonWords = words1.filter(word => words2.includes(word));
  const similarity = commonWords.length / Math.max(words1.length, words2.length);
  
  return similarity;
}

/**
 * Tourism Utility Functions
 * Functions for calculating seasonal discounts and currency conversions
 */

/**
 * Calculate seasonal discount for a tour based on the current month
 * @param {Object} tour - Tour object containing price and other properties
 * @returns {Object} Object with discounted price and discount information
 */
export const roundUsdPrice = (amount) => {
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.round(value * 100) / 100;
};

export const DEFAULT_USD_NPR_RATE = 154.46;

export const calculateSeasonalDiscount = (tour) => {
  const price = roundUsdPrice(tour?.price);
  return {
    originalPrice: price,
    discountedPrice: price,
    discountPercentage: 0,
    discountReason: ''
  };
};

/**
 * Convert USD price to Nepalese Rupees
 * @param {number} priceUSD - Price in USD
 * @returns {number} Price in Nepalese Rupees (rounded to nearest integer)
 */
export const convertToNepaleseRupees = (priceUSD, exchangeRate = DEFAULT_USD_NPR_RATE) => {
  const rate = Number(exchangeRate);
  const safeRate = Number.isFinite(rate) && rate > 0 ? rate : DEFAULT_USD_NPR_RATE;
  return Math.round(roundUsdPrice(priceUSD) * safeRate);
};

/**
 * Round a display-only NPR estimate upward to the next 10 rupees.
 * Confirmed quote and payment amounts must continue using their exact values.
 */
export const roundNprDisplayPrice = (amount) => {
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.ceil(value / 10) * 10;
};

/**
 * Calculate average rating from review scores
 * @param {Array} reviews - Array of review objects with rating property
 * @returns {Object} Object with average rating and count
 */
export const calculateAverageRating = (reviews = []) => {
  if (!reviews || reviews.length === 0) {
    return { average: 0, count: 0 };
  }
  
  const sum = reviews.reduce((total, review) => total + (review.rating || 0), 0);
  const average = sum / reviews.length;
  
  return {
    average: parseFloat(average.toFixed(1)),
    count: reviews.length
  };
};

/**
 * Filter tours by season
 * @param {Array} tours - Array of tour objects
 * @param {string} season - Season to filter by ('winter', 'spring', 'summer', 'autumn')
 * @returns {Array} Filtered tours
 */
export const filterToursBySeason = (tours = [], season) => {
  if (!tours || !Array.isArray(tours)) return [];
  
  return tours.filter(tour => {
    // If tour has explicit seasons property, use that
    if (tour.seasons && Array.isArray(tour.seasons)) {
      return tour.seasons.some(s => s.toLowerCase() === season.toLowerCase());
    }
    
    // Otherwise infer from climate or location
    const { location, climate, description } = tour;
    const seasonLower = season.toLowerCase();
    
    // Check all relevant fields
    const relevantText = [
      location || '',
      climate || '',
      description || '',
      ...(tour.features || []),
      ...(tour.tags || [])
    ].join(' ').toLowerCase();
    
    // Keywords for each season
    const keywords = {
      winter: ['snow', 'skiing', 'winter', 'christmas', 'cold', 'northern lights', 'ice'],
      spring: ['spring', 'flower', 'blossom', 'mild', 'cherry blossom', 'tulip'],
      summer: ['summer', 'beach', 'warm', 'hot', 'sunny', 'swimming', 'tropical'],
      autumn: ['autumn', 'fall', 'foliage', 'leaves', 'harvest', 'cool']
    };
    
    return keywords[seasonLower]?.some(keyword => relevantText.includes(keyword)) || false;
  });
};

/**
 * Get current season based on the month
 * @returns {string} Current season
 */
export const getCurrentSeason = () => {
  const month = new Date().getMonth();
  
  // Northern hemisphere seasons
  if (month >= 2 && month <= 4) return 'Spring';
  if (month >= 5 && month <= 7) return 'Summer';
  if (month >= 8 && month <= 10) return 'Autumn';
  return 'Winter';
};

/**
 * Find the next available tour departures
 * @param {Array} tours - Array of all tours
 * @param {Object} currentTour - The current tour being viewed
 * @param {Date} [startDate=new Date()] - Starting date to search from
 * @param {Number} [maxResults=3] - Maximum number of upcoming departures to return
 * @returns {Array} - Array of upcoming tour departures
 */
export function findNextAvailableTour(tours, currentTour, startDate = new Date(), maxResults = 3) {
  void tours;
  void currentTour;
  void startDate;
  void maxResults;
  // Scheduled departures, remaining places and guarantees must come from the
  // departure service. Never manufacture operational availability in-browser.
  return [];
}

/**
 * Sorts tours using merge sort algorithm
 * @param {Array} tours - Array of tours to sort
 * @param {String} sortBy - Property to sort by (price, days, rating, etc.)
 * @param {Boolean} ascending - Whether to sort in ascending order
 * @returns {Array} - Sorted array of tours
 */
export function mergeSortTours(tours, sortBy = 'price', ascending = true) {
  if (!tours || tours.length <= 1) {
    return tours || [];
  }

  // Handle nested properties with dot notation (e.g., 'location.country')
  const getPropertyValue = (obj, path) => {
    if (!obj) return null;
    const properties = path.split('.');
    return properties.reduce((prev, curr) => 
      prev && prev[curr] !== undefined ? prev[curr] : null, obj);
  };

  // Merge function to combine two sorted arrays
  const merge = (left, right) => {
    const result = [];
    let leftIndex = 0;
    let rightIndex = 0;

    while (leftIndex < left.length && rightIndex < right.length) {
      const leftValue = getPropertyValue(left[leftIndex], sortBy);
      const rightValue = getPropertyValue(right[rightIndex], sortBy);
      
      // Handle string comparisons
      let comparison;
      if (typeof leftValue === 'string' && typeof rightValue === 'string') {
        comparison = leftValue.localeCompare(rightValue);
      } else {
        comparison = leftValue - rightValue;
      }
      
      // Adjust comparison based on sort direction
      const shouldTakeLeft = ascending ? comparison <= 0 : comparison >= 0;
      
      if (shouldTakeLeft) {
        result.push(left[leftIndex]);
        leftIndex++;
      } else {
        result.push(right[rightIndex]);
        rightIndex++;
      }
    }

    // Add remaining elements
    return result
      .concat(left.slice(leftIndex))
      .concat(right.slice(rightIndex));
  };

  // Recursive merge sort implementation
  const mergeSort = (arr) => {
    if (arr.length <= 1) {
      return arr;
    }

    const middle = Math.floor(arr.length / 2);
    const left = arr.slice(0, middle);
    const right = arr.slice(middle);

    return merge(
      mergeSort(left),
      mergeSort(right)
    );
  };

  // Handle missing values - push them to the end regardless of sort direction
  const withValues = tours.filter(tour => getPropertyValue(tour, sortBy) !== null);
  const withoutValues = tours.filter(tour => getPropertyValue(tour, sortBy) === null);
  
  // Sort tours with valid values
  const sortedWithValues = mergeSort(withValues);
  
  // Return sorted array with null values at the end
  return sortedWithValues.concat(withoutValues);
}

/**
 * Sorts tours using quicksort algorithm 
 * @param {Array} tours - Array of tours to sort
 * @param {String} sortBy - Property to sort by (price, days, rating, etc.)
 * @param {Boolean} ascending - Whether to sort in ascending order
 * @returns {Array} - Sorted array of tours
 */
export function quickSortTours(tours, sortBy = 'price', ascending = true) {
  if (!tours || tours.length <= 1) {
    return tours || [];
  }

  // Helper to access potentially nested properties
  const getPropertyValue = (obj, path) => {
    if (!obj) return null;
    const properties = path.split('.');
    return properties.reduce((prev, curr) => 
      prev && prev[curr] !== undefined ? prev[curr] : null, obj);
  };

  // Quicksort implementation
  const quickSort = (arr) => {
    if (arr.length <= 1) {
      return arr;
    }
    
    const pivot = arr[Math.floor(arr.length / 2)];
    const pivotValue = getPropertyValue(pivot, sortBy);
    
    // Create partition arrays
    const less = [];
    const equal = [];
    const greater = [];
    
    // Partition elements
    for (const tour of arr) {
      const tourValue = getPropertyValue(tour, sortBy);
      
      // Handle null values
      if (tourValue === null || tourValue === undefined) {
        greater.push(tour);
        continue;
      }
      
      if (pivotValue === null || pivotValue === undefined) {
        less.push(tour);
        continue;
      }
      
      // Compare values
      let comparison;
      if (typeof tourValue === 'string' && typeof pivotValue === 'string') {
        comparison = tourValue.localeCompare(pivotValue);
      } else {
        comparison = tourValue - pivotValue;
      }
      
      // Place in appropriate partition
      if (ascending ? comparison < 0 : comparison > 0) {
        less.push(tour);
      } else if (ascending ? comparison > 0 : comparison < 0) {
        greater.push(tour);
      } else {
        equal.push(tour);
      }
    }
    
    // Recursively sort partitions and combine
    return [
      ...quickSort(less),
      ...equal,
      ...quickSort(greater)
    ];
  };
  
  return quickSort(tours);
}

/**
 * Utility functions for tour filtering and recommendations
 */

/**
 * Get tours recommendation using ML model
 * @param {Array} tours - Array of tour objects
 * @param {Object} userPreferences - User preferences
 * @returns {Array} Recommended tours
 */
export const getRecommendedTours = (tours, userPreferences) => {
  // This would normally use TensorFlow or other ML model
  // For now, implement a simple algorithm
  if (!tours || !Array.isArray(tours)) return [];
  
  // Default scoring if no preferences provided
  if (!userPreferences) {
    return [...tours].sort((a, b) => {
      const scoreA = (a.rating || 0) * 0.6 + (a.reviewCount || 0) * 0.4;
      const scoreB = (b.rating || 0) * 0.6 + (b.reviewCount || 0) * 0.4;
      return scoreB - scoreA;
    });
  }
  
  // Score each tour based on preferences
  return [...tours].sort((a, b) => {
    let scoreA = 0;
    let scoreB = 0;
    
    // Base score from ratings
    scoreA = (a.rating || 0) * 0.3;
    scoreB = (b.rating || 0) * 0.3;
    
    // Location preference
    if (userPreferences.preferredLocations) {
      const locA = userPreferences.preferredLocations.some(loc => 
        a.location.toLowerCase().includes(loc.toLowerCase())
      );
      const locB = userPreferences.preferredLocations.some(loc => 
        b.location.toLowerCase().includes(loc.toLowerCase())
      );
      scoreA += locA ? 2 : 0;
      scoreB += locB ? 2 : 0;
    }
    
    // Budget preference
    if (userPreferences.budget) {
      scoreA += a.price <= userPreferences.budget ? 1 : -1;
      scoreB += b.price <= userPreferences.budget ? 1 : -1;
    }
    
    // Duration preference
    if (userPreferences.preferredDuration) {
      const durationMatchA = Math.abs(a.duration - userPreferences.preferredDuration);
      const durationMatchB = Math.abs(b.duration - userPreferences.preferredDuration);
      scoreA += 1 - (durationMatchA / 10);
      scoreB += 1 - (durationMatchB / 10);
    }
    
    return scoreB - scoreA;
  });
};
